import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Plus, Search, MapPin, Zap, ChevronRight, Calendar, DollarSign, Filter, X, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  draft: '#94A3B8', planning: '#3B82F6', in_progress: '#F59E0B',
  on_hold: '#EF4444', completed: '#10B981', cancelled: '#6B7280'
};
const statusLabels = {
  draft: 'Borrador', planning: 'Planificación', in_progress: 'En progreso',
  on_hold: 'En espera', completed: 'Completado', cancelled: 'Cancelado'
};

const defaultProject = {
  code: '', name: '', description: '', countryId: '', companyId: '', status: 'draft',
  plantCapacityKwp: '', numModules: '', moduleModel: '', modulePowerWp: '',
  numInverters: '', inverterModel: '', inverterPowerKw: '', numStrings: '',
  structureType: '', hasTransformer: false, transformerModel: '', meterModel: '',
  address: '', city: '', stateProvince: '', postalCode: '', latitude: '', longitude: '',
  contractDate: '', plannedStartDate: '', plannedEndDate: '',
  contractValue: '', currency: 'EUR', prTarget: '80',
  projectManagerId: ''
};

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [form, setForm] = useState({ ...defaultProject });
  const [countries, setCountries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);

  const canCreate = ['superadmin', 'admin_empresa', 'project_manager'].includes(user?.role);

  useEffect(() => {
    loadProjects();
    api.get('/companies/countries/list').then(r => setCountries(r.data)).catch(() => {});
    if (user?.role === 'superadmin') {
      api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    }
    api.get('/users', { params: { limit: 100 } }).then(r => setUsers(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadProjects(); }, [search, statusFilter]);

  const loadProjects = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    api.get('/projects', { params })
      .then(r => setProjects(r.data.data))
      .catch(() => toast.error('Error cargando proyectos'))
      .finally(() => setLoading(false));
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCreate = async (e) => {
    e.preventDefault();
	if (formStep < 3) return setFormStep(formStep + 1);
    if (!form.code || !form.name) return toast.error('Código y nombre son obligatorios');
    setCreating(true);
    try {
      const payload = { ...form };
      // Convert numeric fields
      ['plantCapacityKwp','numModules','modulePowerWp','numInverters','inverterPowerKw','numStrings','contractValue','prTarget','latitude','longitude']
        .forEach(f => { if (payload[f]) payload[f] = parseFloat(payload[f]); });
      const { data } = await api.post('/projects', payload);
      toast.success('Proyecto creado con éxito');
      setShowNew(false);
      setForm({ ...defaultProject });
      setFormStep(1);
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error creando proyecto');
    } finally {
      setCreating(false);
    }
  };

  const openNewProject = () => { setForm({ ...defaultProject }); setFormStep(1); setShowNew(true); };

  const totalKwp = projects.reduce((s, p) => s + (parseFloat(p.plant_capacity_kwp) || 0), 0);

  return (
    <div className="page-projects">
      <div className="page-header">
        <div>
          <h1>Proyectos</h1>
          <p className="subtitle">{projects.length} proyectos · {totalKwp.toLocaleString()} kWp totales</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={openNewProject}>
            <Plus size={18} /> Nuevo proyecto
          </button>
        )}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input placeholder="Buscar por nombre o código..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="clear-search" onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select">
          <option value="">Todos los estados</option>
          {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : (
        <div className="project-grid">
          {projects.map(p => (
            <div key={p.id} className="project-card" onClick={() => navigate(`/projects/${p.id}`)}>
              <div className="project-card-header">
                <span className="project-code">{p.code}</span>
                <span className="status-badge" style={{ background: statusColors[p.status] + '20', color: statusColors[p.status] }}>
                  {statusLabels[p.status]}
                </span>
              </div>
              <h3 className="project-name">{p.name}</h3>
              <div className="project-meta">
                {p.city && <span><MapPin size={14} /> {p.city}{p.country_name ? `, ${p.country_name}` : ''}</span>}
                {p.plant_capacity_kwp && <span><Zap size={14} /> {p.plant_capacity_kwp} kWp</span>}
                {p.planned_end_date && <span><Calendar size={14} /> {new Date(p.planned_end_date).toLocaleDateString('es')}</span>}
              </div>
              <div className="project-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${p.progress_percentage || 0}%` }} />
                </div>
                <span className="progress-text">{Math.round(p.progress_percentage || 0)}%</span>
              </div>
              <div className="project-footer">
                <span className="task-count">{p.completed_tasks || 0}/{p.task_count || 0} tareas</span>
                <span className="milestone-count">{p.completed_milestones || 0}/{p.total_milestones || 0} hitos</span>
                {p.pm_name && <span className="pm-name">{p.pm_name}</span>}
                <ChevronRight size={18} className="arrow" />
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <div className="empty-state">
              <Zap size={48} />
              <h3>No hay proyectos</h3>
              <p>Crea tu primer proyecto de planta fotovoltaica</p>
              {canCreate && <button className="btn btn-primary" onClick={openNewProject}><Plus size={18} /> Crear proyecto</button>}
            </div>
          )}
        </div>
      )}

      {/* NEW PROJECT MODAL - Multi-step */}
      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nuevo Proyecto</h2>
              <button className="btn-close" onClick={() => setShowNew(false)}><X size={20} /></button>
            </div>

            {/* Step indicator */}
            <div className="steps-bar">
              {['Datos básicos', 'Datos técnicos', 'Ubicación y fechas'].map((label, i) => (
                <button key={i} type="button" className={`step ${formStep === i + 1 ? 'active' : formStep > i + 1 ? 'done' : ''}`}
                        onClick={() => setFormStep(i + 1)}>
                  <span className="step-num">{i + 1}</span>
                  <span className="step-label">{label}</span>
                </button>
              ))}
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              {/* STEP 1: Basic data */}
              {formStep === 1 && (
                <div className="form-step">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Código del proyecto *</label>
                      <input required value={form.code} onChange={e => updateForm('code', e.target.value)} placeholder="PV-2025-001" />
                    </div>
                    <div className="form-group">
                      <label>Estado</label>
                      <select value={form.status} onChange={e => updateForm('status', e.target.value)}>
                        {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Nombre del proyecto *</label>
                    <input required value={form.name} onChange={e => updateForm('name', e.target.value)} placeholder="Planta Solar Autoconsumo - Empresa X" />
                  </div>
                  <div className="form-group">
                    <label>Descripción</label>
                    <textarea rows={3} value={form.description} onChange={e => updateForm('description', e.target.value)} placeholder="Descripción del proyecto..." />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>País</label>
                      <select value={form.countryId} onChange={e => updateForm('countryId', e.target.value)}>
                        <option value="">Seleccionar país</option>
                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    {user?.role === 'superadmin' && (
                      <div className="form-group">
                        <label>Empresa</label>
                        <select value={form.companyId} onChange={e => updateForm('companyId', e.target.value)}>
                          <option value="">Seleccionar empresa</option>
                          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Project Manager</label>
                    <select value={form.projectManagerId} onChange={e => updateForm('projectManagerId', e.target.value)}>
                      <option value="">Seleccionar PM</option>
                      {users.filter(u => ['project_manager', 'admin_empresa', 'superadmin'].includes(u.role)).map(u => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* STEP 2: Technical data */}
              {formStep === 2 && (
                <div className="form-step">
                  <h3 className="step-section-title"><Zap size={16} /> Configuración de la planta</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Capacidad total (kWp)</label>
                      <input type="number" step="0.01" value={form.plantCapacityKwp} onChange={e => updateForm('plantCapacityKwp', e.target.value)} placeholder="100.00" />
                    </div>
                    <div className="form-group">
                      <label>PR objetivo (%)</label>
                      <input type="number" step="0.1" value={form.prTarget} onChange={e => updateForm('prTarget', e.target.value)} placeholder="80" />
                    </div>
                  </div>

                  <h4 className="step-subsection">Módulos fotovoltaicos</h4>
                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label>Cantidad módulos</label>
                      <input type="number" value={form.numModules} onChange={e => updateForm('numModules', e.target.value)} placeholder="200" />
                    </div>
                    <div className="form-group">
                      <label>Modelo módulo</label>
                      <input value={form.moduleModel} onChange={e => updateForm('moduleModel', e.target.value)} placeholder="JA Solar JAM72S30-545" />
                    </div>
                    <div className="form-group">
                      <label>Potencia módulo (Wp)</label>
                      <input type="number" step="0.01" value={form.modulePowerWp} onChange={e => updateForm('modulePowerWp', e.target.value)} placeholder="545" />
                    </div>
                  </div>

                  <h4 className="step-subsection">Inversores</h4>
                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label>Cantidad inversores</label>
                      <input type="number" value={form.numInverters} onChange={e => updateForm('numInverters', e.target.value)} placeholder="2" />
                    </div>
                    <div className="form-group">
                      <label>Modelo inversor</label>
                      <input value={form.inverterModel} onChange={e => updateForm('inverterModel', e.target.value)} placeholder="Huawei SUN2000-50KTL" />
                    </div>
                    <div className="form-group">
                      <label>Potencia inversor (kW)</label>
                      <input type="number" step="0.01" value={form.inverterPowerKw} onChange={e => updateForm('inverterPowerKw', e.target.value)} placeholder="50" />
                    </div>
                  </div>

                  <h4 className="step-subsection">Estructura y cableado</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Número de strings</label>
                      <input type="number" value={form.numStrings} onChange={e => updateForm('numStrings', e.target.value)} placeholder="10" />
                    </div>
                    <div className="form-group">
                      <label>Tipo de estructura</label>
                      <select value={form.structureType} onChange={e => updateForm('structureType', e.target.value)}>
                        <option value="">Seleccionar</option>
                        <option value="coplanar">Coplanar</option>
                        <option value="inclinada">Inclinada fija</option>
                        <option value="tracker_1eje">Tracker 1 eje</option>
                        <option value="tracker_2ejes">Tracker 2 ejes</option>
                        <option value="marquesina">Marquesina</option>
                        <option value="suelo">Suelo</option>
                      </select>
                    </div>
                  </div>

                  <h4 className="step-subsection">Equipos adicionales</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Modelo medidor</label>
                      <input value={form.meterModel} onChange={e => updateForm('meterModel', e.target.value)} placeholder="Schneider PM5110" />
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input type="checkbox" checked={form.hasTransformer} onChange={e => updateForm('hasTransformer', e.target.checked)} />
                        <span>¿Requiere transformador?</span>
                      </label>
                    </div>
                  </div>
                  {form.hasTransformer && (
                    <div className="form-group">
                      <label>Modelo transformador</label>
                      <input value={form.transformerModel} onChange={e => updateForm('transformerModel', e.target.value)} placeholder="Schneider Trihal 630kVA" />
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3: Location, dates, financials */}
              {formStep === 3 && (
                <div className="form-step">
                  <h3 className="step-section-title"><MapPin size={16} /> Ubicación</h3>
                  <div className="form-group">
                    <label>Dirección</label>
                    <input value={form.address} onChange={e => updateForm('address', e.target.value)} placeholder="Polígono Industrial X, Nave 5" />
                  </div>
                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label>Ciudad</label>
                      <input value={form.city} onChange={e => updateForm('city', e.target.value)} placeholder="Madrid" />
                    </div>
                    <div className="form-group">
                      <label>Provincia/Estado</label>
                      <input value={form.stateProvince} onChange={e => updateForm('stateProvince', e.target.value)} placeholder="Madrid" />
                    </div>
                    <div className="form-group">
                      <label>Código postal</label>
                      <input value={form.postalCode} onChange={e => updateForm('postalCode', e.target.value)} placeholder="28001" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Latitud</label>
                      <input type="number" step="any" value={form.latitude} onChange={e => updateForm('latitude', e.target.value)} placeholder="40.4168" />
                    </div>
                    <div className="form-group">
                      <label>Longitud</label>
                      <input type="number" step="any" value={form.longitude} onChange={e => updateForm('longitude', e.target.value)} placeholder="-3.7038" />
                    </div>
                  </div>

                  <h3 className="step-section-title"><Calendar size={16} /> Fechas</h3>
                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label>Fecha contrato</label>
                      <input type="date" value={form.contractDate} onChange={e => updateForm('contractDate', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Inicio planificado</label>
                      <input type="date" value={form.plannedStartDate} onChange={e => updateForm('plannedStartDate', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Fin planificado</label>
                      <input type="date" value={form.plannedEndDate} onChange={e => updateForm('plannedEndDate', e.target.value)} />
                    </div>
                  </div>

                  <h3 className="step-section-title"><DollarSign size={16} /> Financiero</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Valor del contrato</label>
                      <input type="number" step="0.01" value={form.contractValue} onChange={e => updateForm('contractValue', e.target.value)} placeholder="150000.00" />
                    </div>
                    <div className="form-group">
                      <label>Moneda</label>
                      <select value={form.currency} onChange={e => updateForm('currency', e.target.value)}>
                        <option value="EUR">EUR - Euro</option>
                        <option value="USD">USD - Dólar</option>
                        <option value="MXN">MXN - Peso MX</option>
                        <option value="COP">COP - Peso CO</option>
                        <option value="CLP">CLP - Peso CL</option>
                        <option value="BRL">BRL - Real</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="form-actions">
                {formStep > 1 && (
                  <button type="button" className="btn btn-secondary" onClick={() => setFormStep(formStep - 1)}>Anterior</button>
                )}
                <div style={{ flex: 1 }} />
                {formStep < 3 ? (
                  <button type="button" className="btn btn-primary" onClick={() => {
                    if (formStep === 1 && (!form.code || !form.name)) return toast.error('Código y nombre son obligatorios');
                    setFormStep(formStep + 1);
                  }}>Siguiente</button>
                ) : (
                  <button type="button" className="btn btn-primary" disabled={creating} onClick={handleCreate}>
                    {creating ? 'Creando...' : 'Crear proyecto'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
